import { httpsCallable } from 'firebase/functions';
import { functions } from '../config/firebase';

const aceptarFn = httpsCallable(functions, 'portal_aceptarConsulta');
const finalizarFn = httpsCallable(functions, 'portal_finalizarConsulta');
const crearFn = httpsCallable(functions, 'portal_crearConsulta');
const procesarTranscripcionFn = httpsCallable(functions, 'portal_procesarTranscripcion');
const extenderSesionFn = httpsCallable(functions, 'portal_extenderSesion');
const agregarNotaPostFn = httpsCallable(functions, 'portal_agregarNotaPostConsulta');

export async function agregarNotaPostConsulta(consultaId: string, texto: string) {
  const result = await agregarNotaPostFn({ consultaId, texto, durante: false });
  return result.data as {
    success: boolean;
    notaId: string;
    horasRestantes: number;
  };
}

/**
 * Persiste el texto RAW de una nota que el médico escribe durante la
 * consulta en vivo. No pasa por IA — se guarda como tipo='nota_doctor_vivo'.
 * Esto preserva la intención original del doctor aunque la IA falle o
 * transforme el texto.
 */
export async function persistirNotaDoctorVivo(consultaId: string, texto: string) {
  const result = await agregarNotaPostFn({ consultaId, texto, durante: true });
  return result.data as {
    success: boolean;
    notaId: string;
    horasRestantes: null;
  };
}

export async function extenderSesion(consultaId: string, minutos: 5 | 10 | 15) {
  const result = await extenderSesionFn({ consultaId, minutos });
  return result.data as {
    success: boolean;
    sessionEndsAt: number;
    extensionesUsadas: number;
    extensionesRestantes: number;
  };
}

export async function aceptarConsulta(consultaId: string) {
  const result = await aceptarFn({ consultaId });
  return result.data as { success: boolean; consultaId: string };
}

// COLA-02: aceptar/rechazar una OFERTA del modo Híbrido (estado 'asignada').
const aceptarAsignacionFn = httpsCallable(functions, 'portal_aceptarAsignacion');
const rechazarAsignacionFn = httpsCallable(functions, 'portal_rechazarAsignacion');

export async function aceptarAsignacion(consultaId: string) {
  const result = await aceptarAsignacionFn({ consultaId });
  return result.data as { success: boolean; consultaId: string };
}

export async function rechazarAsignacion(consultaId: string, motivo?: string) {
  const result = await rechazarAsignacionFn({ consultaId, motivo });
  return result.data as { success: boolean };
}

// Devolver a la cola una oferta ('asignada') o consulta en curso — el propio
// médico, o un admin en su nombre (p.ej. desde el tab "Ofertas pendientes").
const devolverConsultaAColaFn = httpsCallable(functions, 'portal_devolverConsultaACola');

export async function devolverConsultaACola(consultaId: string) {
  const result = await devolverConsultaAColaFn({ consultaId });
  return result.data as { success: boolean };
}

// REC-01: iniciar la grabación (egress de audio) de la consulta. Idempotente.
const crearGrabacionFn = httpsCallable(functions, 'portal_crearSalaConGrabacion');

export async function iniciarGrabacion(consultaId: string) {
  const result = await crearGrabacionFn({ consultaId });
  return result.data as {
    egressId?: string;
    alreadyRecording?: boolean;
    grabacionDeshabilitada?: boolean;
  };
}

export async function finalizarConsulta(data: {
  consultaId: string;
  disposicion: string;
  observaciones?: string;
  calidad?: number;
  requiereSeguimiento?: boolean;
}) {
  const result = await finalizarFn(data);
  return result.data as { success: boolean; duracionSegundos: number };
}

export async function crearConsulta(data: {
  pacienteNombre: string;
  pacienteId?: string;
  motivoConsulta: string;
  salaId: string;
}) {
  const result = await crearFn(data);
  return result.data as { consultaId: string };
}

// CAP: marca al médico disponible/offline en una sala — esto es lo que
// alimenta `medicosActivos` de la sala, que el cron de capacidad dinámica
// (reconciliarCuposEnVivo) usa para calcular cuántos médicos hay en turno.
const medicoEntrarSalaFn = httpsCallable(functions, 'portal_medicoEntrarSala');
const medicoSalirSalaFn = httpsCallable(functions, 'portal_medicoSalirSala');

export async function medicoEntrarSala(salaId: string) {
  const result = await medicoEntrarSalaFn({ salaId });
  return result.data as { success: boolean };
}

export async function medicoSalirSala(salaId: string) {
  const result = await medicoSalirSalaFn({ salaId });
  return result.data as { success: boolean };
}

// M8: break de almuerzo — excluye al médico del pool de auto-asignación
// mientras dura, sin marcarlo offline (ver functions/src/consultas/presenceMedico.ts).
const iniciarBreakAlmuerzoFn = httpsCallable(functions, 'portal_iniciarBreakAlmuerzo');
const terminarBreakAlmuerzoFn = httpsCallable(functions, 'portal_terminarBreakAlmuerzo');

export async function iniciarBreakAlmuerzo() {
  const result = await iniciarBreakAlmuerzoFn({});
  return result.data as { success: boolean };
}

export async function terminarBreakAlmuerzo() {
  const result = await terminarBreakAlmuerzoFn({});
  return result.data as { success: boolean };
}

// Cancelación masiva de pacientes en espera por un supervisor/admin.
const cancelarConsultasSupervisorFn = httpsCallable(functions, 'portal_cancelarConsultasSupervisor');

export async function cancelarConsultasSupervisor(consultaIds: string[], motivo: string) {
  const result = await cancelarConsultasSupervisorFn({ consultaIds, motivo });
  return result.data as {
    resultados: Array<{ consultaId: string; success: boolean; error?: string }>;
  };
}

export async function procesarTranscripcionIA(data: {
  consultaId: string;
  transcripcion: string;
  contexto?: string;
}) {
  const result = await procesarTranscripcionFn(data);
  return result.data as {
    success: boolean;
    notaId: string;
    soap: { subjective: string; objective: string; assessment: string; plan: string };
    fhir: Array<{ code: string; display: string; system: string }>;
    alerts: Array<{ severity: string; message: string }>;
    healthEducation: string[];
  };
}
